<!--
 * Path: src/features/browser/forms/markdown/MarkdownRead.vue
 * Task: Parses and displays markdown text in read-only mode, conforming to active theme text and border colors.
 * Update: Removed hardcoded system font stacks to strictly inherit Vuetify's Roboto typography.
-->
<template>
  <div :class="{ 'mb-6': !hideHeader }">
    <!-- Header conditionally rendered -->
    <SubHeader
      v-if="!hideHeader"
      :title="label"
      :info="info"
      @click:info="$emit('click:info')"
    />

    <!-- Rendered content -->
    <div
      class="markdown-preview read-only ms-1 text-on-surface text-body-1"
      v-html="renderedMarkdown"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import SubHeader from '@/core/components/forms/SubHeader.vue';

interface Props {
  modelValue?: string;
  label?: string;
  info?: string;
  hideHeader?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: '',
  info: '',
  hideHeader: false
});

defineEmits<{
  (e: 'click:info'): void;
}>();

const renderedMarkdown = computed<string>(() => {
  marked.setOptions({ gfm: true, breaks: true });
  const rawHtml = marked.parse(props.modelValue || `*${props.label || 'Content'} is empty.*`) as string;
  return DOMPurify.sanitize(rawHtml);
});
</script>

<style scoped>
/* Styles specific for read-only view */
.markdown-preview {
  line-height: 1.6;
}

.markdown-preview.read-only {
  min-height: auto;
  color: inherit; /* Inherits reactive theme text colors */
}

.markdown-preview.read-only :deep(p:last-child) {
  margin-bottom: 0;
}

/* Markdown Element Styles adapting to system borders and text */
.markdown-preview :deep(h1),
.markdown-preview :deep(h2),
.markdown-preview :deep(h3) {
  margin-top: 12px;
  margin-bottom: 8px;
  font-weight: 600;
  line-height: 1.25;
}

.markdown-preview :deep(h1) {
  font-size: 1.5em;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-bottom: 0.3em;
}

.markdown-preview :deep(h2) {
  font-size: 1.3em;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-bottom: 0.3em;
}

.markdown-preview :deep(h3) {
  font-size: 1.1em;
}

.markdown-preview :deep(ul),
.markdown-preview :deep(ol) {
  padding-left: 20px;
  margin-bottom: 12px;
}

.markdown-preview :deep(li) {
  margin-bottom: 4px;
}

.markdown-preview :deep(p) {
  margin-bottom: 12px;
}

.markdown-preview :deep(*:last-child) {
  margin-bottom: 0;
}

.markdown-preview :deep(a) {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}

.markdown-preview :deep(a):hover {
  text-decoration: underline;
}

.markdown-preview :deep(blockquote) {
  border-left: 4px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-left: 16px;
  margin-left: 0;
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-style: italic;
}

.markdown-preview :deep(code) {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
  color: rgb(var(--v-theme-on-surface));
  padding: 2px 4px;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.9em;
}
</style>
